/**
 * Session-storage persistence for admin table state.
 *
 * Every admin table persists the same six slices under its own key prefix, so
 * the reader/writer pair is created once per table rather than re-declared.
 */

/** Storage keys each table persists under its own prefix. */
export const TABLE_STATE_KEYS = {
  FILTERS: 'filters',
  SORTING: 'sorting',
  COLUMN_VISIBILITY: 'column-visibility',
  COLUMN_ORDER: 'column-order',
  COLUMN_WRAPPING: 'column-wrapping',
  VISIBLE_COUNT: 'visible-count',
} as const

export interface TableSessionStore {
  /**
   * Read a value without subscribing to changes — for `useState` initializers.
   *
   * Returns the stored value only where it has `defaultValue`'s shape, field
   * by field for objects; the default fills anything missing or mismatched.
   * Three things carry no shape to check against and pass through as stored:
   * a field whose default is `null` or `undefined` (at any depth), keys the
   * default does not name (so a `{}` Record default keeps its entries), and
   * the elements of an array default.
   */
  getValue: <T>(key: string, defaultValue: T) => T
  setValue: <T>(key: string, value: T) => void
}

/** `typeof`, with the three JSON shapes it conflates pulled apart. */
function shapeOf(value: unknown) {
  if (value === null) return 'null'
  if (Array.isArray(value)) return 'array'
  return typeof value
}

function isPlainObject(value: unknown): value is Record<string, unknown> {
  return shapeOf(value) === 'object'
}

/**
 * sessionStorage outlives deploys, which makes this the seam between
 * yesterday's build and today's `useState` initializer: a filter field added
 * or retyped since the value was written used to arrive `undefined` and crash
 * the table on its first `.length` (LG-605). Contract: see
 * `TableSessionStore.getValue`.
 */
function reconcile<T>(stored: unknown, defaultValue: T): T {
  const expected = shapeOf(defaultValue)
  if (expected === 'null' || expected === 'undefined') return stored as T
  if (shapeOf(stored) !== expected) return defaultValue
  if (!isPlainObject(defaultValue) || !isPlainObject(stored)) return stored as T

  const merged: Record<string, unknown> = { ...stored }
  for (const key of Object.keys(defaultValue)) {
    merged[key] =
      key in stored
        ? reconcile(stored[key], defaultValue[key])
        : defaultValue[key]
  }
  return merged as T
}

export function createTableSessionStore(prefix: string): TableSessionStore {
  const fullKey = (key: string) => `${prefix}-${key}`

  return {
    getValue<T>(key: string, defaultValue: T): T {
      if (typeof window === 'undefined') {
        return defaultValue
      }

      try {
        const stored = sessionStorage.getItem(fullKey(key))
        if (stored !== null) {
          return reconcile(JSON.parse(stored), defaultValue)
        }
      } catch {
        // If parsing fails, use default
      }

      return defaultValue
    },

    setValue<T>(key: string, value: T): void {
      if (typeof window === 'undefined') return

      try {
        sessionStorage.setItem(fullKey(key), JSON.stringify(value))
      } catch {
        // Session storage might be full or disabled
      }
    },
  }
}
